/*
 * <insurance-modal-v2> - insurance coverage modal v2 (SD-26-354).
 *
 * Flow (views are `data-view` sections in snippets/product-insurance-modal-v2.liquid):
 *
 *   hmo  --Yes-->  hmo_rejected (cash card + Add to Cart)
 *    |--No / I don't know-->  verify (shipping state + insurance combobox)
 *
 *   verify, Continue:
 *     "I don't see mine"            -> inline not-covered message (Continue stays disabled)
 *     Medicare / Medicaid / Medi-Cal (payer NAME) -> never checked in-house (Canvas
 *                                          cannot serve them); straight to the Medicare flow:
 *                                          medicare_mode legacy: iSleep contact form
 *                                          medicare_mode custom: merchant message + link
 *     everyone else POSTs {worker}/route {payerId, state, payerName}
 *       (in-house states come only from the worker's CANVAS_ENABLED_STATES)
 *       bridgeCovered = npCovered || pcCovered
 *       route === 'canvas'                 -> quote  (Get My Quote = Canvas Zoho)
 *       bridgeCovered OR iSleep-list match -> accepted (cash card FIRST in async_states)
 *       neither                            -> inline not-covered message
 *       /route error                       -> iSleep-list fallback; no match -> error view
 *
 * Coverage order: Bridge first, then the iSleep carrier list
 * (snippets/insurance-isleep-carriers.liquid), else cash pay.
 *
 * TRACKING: every interactive element in the markup carries data-track="<event_action>".
 * One delegated listener (capture phase, so it runs before the element's own behavior)
 * pushes `event: 'insurance_modal_v2'`. Events that are not a direct click (step_viewed,
 * coverage_check_*, result_viewed, worker_error, insurance_search, modal_open,
 * modal_abandoned, Escape-close, ...) are pushed by the controller. No PII is pushed;
 * email only ever leaves as user_email_hash.
 *
 * Config arrives as data-* attributes. Redirect ids come from window.SDFormTracking
 * (assets/sd-form-tracking.js).
 */
(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 749;
  const SEARCH_DEBOUNCE_MS = 250;
  const SEARCH_MIN_CHARS = 2;
  const EVENT_NAME = 'insurance_modal_v2';

  /* Payers that always take the Medicare path, checked before any API call. */
  const MEDICARE_NAME_RE = /medicare|medicaid|medi-?cal/i;

  /* DOM event each data-track action listens on. Anything not listed is a click. */
  const EVENT_FOR_ACTION = {
    hmo_answer_selected: 'change',
    state_selected: 'change',
    insurance_dropdown_open: 'focusin',
    form_field: 'focusin focusout',
  };
  const DELEGATED_EVENTS = ['click', 'change', 'focusin', 'focusout'];

  function appendParam(url, key, value) {
    if (!url || !value) return url;
    return (
      url +
      (url.indexOf('?') > -1 ? '&' : '?') +
      encodeURIComponent(key) +
      '=' +
      encodeURIComponent(value)
    );
  }

  /* Sticky 0-99 bucket for the intake split, so a visitor keeps the same form across
     visits. Falls back to a per-page-load bucket when storage is blocked. */
  const INTAKE_BUCKET_KEY = 'sd_intake_split_bucket';
  let memoryBucket = null;
  function intakeSplitBucket() {
    try {
      const stored = parseInt(window.localStorage.getItem(INTAKE_BUCKET_KEY), 10);
      if (stored >= 0 && stored < 100) return stored;
      const fresh = Math.floor(Math.random() * 100);
      window.localStorage.setItem(INTAKE_BUCKET_KEY, String(fresh));
      return fresh;
    } catch {
      if (memoryBucket === null) memoryBucket = Math.floor(Math.random() * 100);
      return memoryBucket;
    }
  }

  /* Merchant-typed URL settings are plain text fields, so "forms.example.com/x" (no
     scheme) would otherwise be resolved as a path on the current page
     (sleepdoctor.com/pages/forms.example.com/x). Treat anything that starts with a
     domain as https; leave full URLs and site-relative paths ("/cart/...") alone. */
  function toAbsoluteUrl(raw) {
    const value = String(raw || '').trim();
    if (!value) return '';
    if (/^[a-z][a-z0-9+.-]*:/i.test(value) || /^[/?#]/.test(value)) return value;
    if (/^[^\s/]+\.[a-z]{2,}(?:[/:?#]|$)/i.test(value)) return 'https://' + value;
    return value;
  }

  function parseStates(raw) {
    return (raw || '')
      .split(',')
      .map(function (s) { return s.trim().toUpperCase(); })
      .filter(Boolean);
  }

  /* Lowercase, strip punctuation, collapse whitespace. */
  function normalize(text) {
    return (' ' + String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() + ' ');
  }

  class InsuranceModalV2 extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.surface = this.getAttribute('data-surface') || 'product';
      this.sectionId = this.getAttribute('data-section-id') || '';
      this.workerUrl = toAbsoluteUrl(this.getAttribute('data-worker-url')).replace(/\/$/, '');
      this.canvasFormUrl = toAbsoluteUrl(this.getAttribute('data-canvas-form-url'));
      this.standardIntakeUrl = toAbsoluteUrl(this.getAttribute('data-standard-intake-url'));
      /* Intake split (Theme settings > Insurance intake split): a share of iSleep
         "Submit Insurance Form" traffic goes to the new Zoho Creator intake. */
      this.intakeSplitUrl = toAbsoluteUrl(this.getAttribute('data-intake-split-url'));
      this.intakeSplitPercent = parseInt(this.getAttribute('data-intake-split-percent'), 10) || 0;
      this.intakeSplitStates = parseStates(this.getAttribute('data-intake-split-states'));
      this.payerParam = this.getAttribute('data-payer-param') || 'PayorID';
      this.primaryPayorParam = this.getAttribute('data-primary-payor-param') || 'PrimaryPayor';
      this.asyncStates = parseStates(this.getAttribute('data-async-states'));
      this.checkingText = this.getAttribute('data-checking-text') || 'Checking coverage…';
      this.searchingText = this.getAttribute('data-searching-text') || 'Searching…';
      this.noResultsText = this.getAttribute('data-no-results-text') || 'No matching insurance found.';
      this.searchErrorText =
        this.getAttribute('data-search-error-text') || "We couldn't load insurance options. Try again.";
      this.searchHint = this.getAttribute('data-search-hint') || 'Type at least 2 letters to search';
      this.notListedLabel = this.getAttribute('data-not-listed-label') || "I don't see mine";
      this.externalTriggerSelector = this.getAttribute('data-external-trigger') || '';
      this.medicareMode = this.getAttribute('data-medicare-mode') || 'legacy';
      this.medicareCustomUrl = toAbsoluteUrl(this.getAttribute('data-medicare-custom-url'));
      this.dreemWorkerUrl = toAbsoluteUrl(this.getAttribute('data-dreem-worker-url'));
      this.dreemRedirectUrl = toAbsoluteUrl(this.getAttribute('data-dreem-redirect-url'));
      this.cashCartUrl = this.getAttribute('data-cash-cart-url') || '';
      this.cashPrice = this.getAttribute('data-cash-price') || '';

      this.carriers = this.readCarriers();

      this.root = this.querySelector('[data-root]');
      this.dialog = this.querySelector('[data-dialog]');
      this.views = {};
      this.querySelectorAll('[data-view]').forEach((v) => {
        this.views[v.getAttribute('data-view')] = v;
      });

      this.hmoRadios = this.querySelectorAll('[data-hmo-answer]');
      this.hmoContinue = this.querySelector('[data-hmo-continue]');
      this.verifyForm = this.querySelector('[data-verify-form]');
      this.stateSelect = this.querySelector('[data-state-select]');
      this.insuranceField = this.querySelector('[data-insurance-field]');
      this.comboInput = this.querySelector('[data-combo-input]');
      this.comboList = this.querySelector('[data-combo-list]');
      this.notCoveredEl = this.querySelector('[data-not-covered]');
      this.verifyContinue = this.querySelector('[data-verify-continue]');
      this.verifyContinueLabel = this.querySelector('[data-verify-continue-label]');
      this.verifyContinueDefault = this.verifyContinueLabel ? this.verifyContinueLabel.textContent : '';
      this.cards = this.querySelector('[data-cards]');
      this.isleepForm = this.querySelector('[data-isleep-form]');
      this.isleepSubmit = this.querySelector('[data-isleep-submit]');
      this.isleepSubmitLabel = this.querySelector('[data-isleep-submit-label]');
      this.isleepSubmitDefault = this.isleepSubmitLabel ? this.isleepSubmitLabel.textContent : '';

      this.resetState();
      this.view = 'pre_modal';
      this.history = [];
      this.searchSeq = 0;
      this.searchTimer = null;
      this.isOpen = false;
      this.converted = false;
      this.isleepFormStarted = false;
      this.stateOpenTracked = false;

      /* No cash URL configured: remove the cash escapes rather than leave dead buttons. */
      if (!this.cashCartUrl) {
        this.querySelectorAll('[data-cash]').forEach((el) => el.remove());
      }

      this.bindTracking();
      this.bindBehavior();

      /* Deep link: ?modal=true opens on load. */
      try {
        if (new URLSearchParams(window.location.search).get('modal') === 'true') {
          this.push('open_button_click', { trigger: 'url_param' });
          this.openModal();
        }
      } catch {
        /* URL parsing is best-effort; never block render */
      }
    }

    /* ------------------------------------------------------------------ */
    /* Config                                                             */
    /* ------------------------------------------------------------------ */

    readCarriers() {
      const el = this.querySelector('script[data-isleep-carriers]');
      if (!el) return [];
      try {
        const list = JSON.parse(el.textContent);
        return Array.isArray(list) ? list : [];
      } catch {
        return [];
      }
    }

    resetState() {
      this.hmoAnswer = '';
      this.shippingState = '';
      this.payerId = '';
      this.payerName = '';
      this.notListed = false;
      this.notCovered = false;
      this.results = [];
      this.coverage = null;
    }

    /* ------------------------------------------------------------------ */
    /* Tracking                                                           */
    /* ------------------------------------------------------------------ */

    /* Base payload + selections so far + per-event extras (extras win). */
    push(action, extra) {
      window.dataLayer = window.dataLayer || [];
      const payload = {
        event: EVENT_NAME,
        modal_version: 'v2',
        event_action: action,
        device_type: window.innerWidth <= MOBILE_BREAKPOINT ? 'mobile' : 'desktop',
        surface: this.surface,
        section_id: this.sectionId,
        modal_step: this.view,
      };
      if (this.hmoAnswer) payload.hmo_answer = this.hmoAnswer;
      if (this.shippingState) payload.shipping_state = this.shippingState;
      if (this.payerName) payload.insurance_carrier = this.payerName;
      if (this.payerId) payload.payer_id = this.payerId;
      window.dataLayer.push(Object.assign(payload, extra || {}));
    }

    bindTracking() {
      DELEGATED_EVENTS.forEach((type) => {
        this.addEventListener(type, (e) => this.onDomEvent(e), true);
      });
    }

    onDomEvent(e) {
      const el = e.target && e.target.closest ? e.target.closest('[data-track]') : null;
      if (!el || !this.contains(el)) return;
      const action = el.getAttribute('data-track');
      const types = (EVENT_FOR_ACTION[action] || 'click').split(' ');
      if (types.indexOf(e.type) === -1) return;
      /* Programmatic refocus after picking an option is not the shopper opening the list. */
      if (action === 'insurance_dropdown_open' && this.suppressOpenTrack) {
        this.suppressOpenTrack = false;
        return;
      }

      switch (action) {
        case 'form_field':
          if (e.type === 'focusin') {
            if (!this.isleepFormStarted) {
              this.isleepFormStarted = true;
              this.push('form_started');
            }
          } else if (el.value && el.value.trim() !== '') {
            this.push('form_field_completed', {
              form_field_name: (el.name || '').replace(/^contact\[(\w+)\]$/, '$1') || el.id,
            });
          }
          return;
        case 'open_button_click':
          this.push(action, { trigger: el.getAttribute('data-trigger') || 'builtin' });
          return;
        case 'modal_close':
          this.push(action, {
            close_method: el.getAttribute('data-close-method') || 'x',
            last_step: this.view,
          });
          return;
        case 'back_click':
          this.push(action, { from_step: this.view, to_step: this.previousView() });
          return;
        case 'how_it_works_open':
          this.push(action, { from_step: this.view });
          return;
        case 'how_it_works_close':
          this.push(action, { from_step: this.view, to_step: this.previousView() });
          return;
        case 'hmo_answer_selected':
          this.push(action, { hmo_answer: el.value });
          return;
        case 'state_selected':
          this.push(action, { shipping_state: el.value });
          return;
        case 'insurance_selected':
          this.push(action, {
            selection_source: el.getAttribute('data-selection-source') || 'bridge',
            insurance_carrier: el.getAttribute('data-payer-name') || '',
            payer_id: el.getAttribute('data-payer-id') || '',
          });
          return;
        case 'add_to_cart_click':
          this.push(action, {
            placement: el.getAttribute('data-placement') || '',
            cash_price: this.cashPrice,
          });
          return;
        case 'get_my_quote_click':
          this.push(action, { intake_destination: 'canvas', redirect_url: this.canvasFormUrl });
          return;
        case 'submit_insurance_form_click': {
          const intake = this.resolveStandardIntake();
          this.push(action, {
            intake_destination: intake.destination,
            redirect_url: intake.url,
            intake_split_percent: this.intakeSplitPercent,
          });
          return;
        }
        case 'medicare_custom_cta_click':
          this.push(action, { redirect_url: this.medicareCustomUrl });
          return;
        default:
          this.push(action);
      }
    }

    /* ------------------------------------------------------------------ */
    /* Wiring                                                             */
    /* ------------------------------------------------------------------ */

    bindBehavior() {
      const cta = this.querySelector('.imv2__cta');
      if (cta) cta.addEventListener('click', () => this.openModal());

      /* External triggers: existing page buttons open the modal. Delegated on document so
         it catches elements injected later. */
      if (this.externalTriggerSelector) {
        document.addEventListener('click', (e) => {
          const trigger =
            e.target && e.target.closest ? e.target.closest(this.externalTriggerSelector) : null;
          if (!trigger) return;
          e.preventDefault();
          this.push('open_button_click', { trigger: 'external' });
          this.openModal();
        });
      }

      /* Close: X and backdrop (their data-track pushes modal_close). */
      this.querySelector('.imv2__close').addEventListener('click', () => this.closeModal());
      this.querySelector('.imv2__backdrop').addEventListener('click', () => this.closeModal());

      /* Navigation */
      this.querySelectorAll('[data-back]').forEach((btn) => {
        btn.addEventListener('click', () => this.goBack());
      });
      this.querySelectorAll('[data-how-open]').forEach((btn) => {
        btn.addEventListener('click', () => this.openHow());
      });
      this.querySelector('[data-how-close]').addEventListener('click', () => this.closeHow());

      /* Step 1 */
      this.hmoRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          this.hmoAnswer = radio.value;
          this.hmoRadios.forEach((r) => {
            r.closest('.imv2__chk').classList.toggle('is-on', r.checked);
          });
          this.hmoContinue.disabled = false;
        });
      });
      this.hmoContinue.addEventListener('click', () => {
        if (this.hmoContinue.disabled) return;
        if (this.hmoAnswer === 'yes') {
          this.showView('hmo_rejected');
          this.push('result_viewed', { result_type: 'hmo_rejected' });
        } else {
          this.showView('verify');
        }
      });

      /* Step 2 */
      this.initState();
      this.initCombo();
      this.verifyForm.addEventListener('submit', (e) => {
        e.preventDefault();
        if (this.verifyContinue.disabled) return;
        this.onVerifyContinue();
      });

      /* Conversions */
      this.querySelectorAll('[data-cash]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.goToCash();
        });
      });
      const quoteBtn = this.querySelector('[data-get-quote]');
      if (quoteBtn) quoteBtn.addEventListener('click', () => this.submitCanvas());
      const insBtn = this.querySelector('[data-submit-insurance]');
      if (insBtn) insBtn.addEventListener('click', () => this.submitStandardIntake());
      const medicareCta = this.querySelector('[data-medicare-cta]');
      if (medicareCta) {
        medicareCta.addEventListener('click', (e) => {
          e.preventDefault();
          this.converted = true;
          this.redirect(this.medicareCustomUrl);
        });
      }

      /* Legacy Medicare form */
      if (this.isleepForm) {
        this.isleepForm.addEventListener('submit', (e) => {
          e.preventDefault();
          this.submitIsleep();
        });
        this.isleepForm.querySelectorAll('input').forEach((input) => {
          input.addEventListener('input', () => {
            const group = input.closest('.imv2__form-group');
            if (group) group.classList.remove('has-error');
            input.removeAttribute('aria-invalid');
          });
        });
      }

      document.addEventListener('keydown', (e) => this.onKeydown(e));
      document.addEventListener('click', (e) => {
        if (this.isOpen && !e.target.closest('[data-combo]')) this.closeList();
      });
    }

    onKeydown(e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        /* Escape closes an open listbox first, the modal second. */
        if (!this.comboList.hidden) {
          this.closeList();
          return;
        }
        this.push('modal_close', { close_method: 'escape', last_step: this.view });
        this.closeModal();
        return;
      }
      if (e.key === 'Tab') this.trapFocus(e);
    }

    trapFocus(e) {
      const focusable = Array.prototype.filter.call(
        this.dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
        (el) => !el.disabled && !el.closest('[inert]') && el.offsetParent !== null && !el.closest('[hidden]')
      );
      if (!focusable.length) {
        e.preventDefault();
        this.dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === this.dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (!this.dialog.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ------------------------------------------------------------------ */
    /* Open / close / views                                               */
    /* ------------------------------------------------------------------ */

    openModal() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.converted = false;
      this.lastFocus = document.activeElement;
      this.view = 'hmo';
      this.history = [];
      this.root.hidden = false;
      document.body.style.overflow = 'hidden';
      this.showView('hmo', { track: false });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.root.classList.add('is-open'));
      });
      this.push('modal_open');
      this.push('step_viewed');
    }

    closeModal() {
      if (!this.isOpen) return;
      /* modal_close itself is pushed by the caller (delegated tracker or Escape handler). */
      if (!this.converted) this.push('modal_abandoned', { last_step: this.view });
      this.isOpen = false;
      this.root.classList.remove('is-open');
      document.body.style.overflow = '';
      setTimeout(() => {
        if (this.isOpen) return;
        this.root.hidden = true;
        this.resetModal();
      }, 200);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
      this.view = 'pre_modal';
    }

    /* Show one view; inert + hidden for every other. opts.track=false skips step_viewed. */
    showView(name, opts) {
      const options = opts || {};
      Object.keys(this.views).forEach((key) => {
        const active = key === name;
        this.views[key].classList.toggle('is-active', active);
        if (active) {
          this.views[key].removeAttribute('inert');
        } else {
          this.views[key].setAttribute('inert', '');
        }
      });
      if (options.pushHistory !== false && this.view !== name && this.view !== 'pre_modal' && options.back !== true) {
        this.history.push(this.view);
      }
      this.view = name;
      this.dialog.setAttribute('data-view-active', name);
      const heading = this.views[name].querySelector('h2');
      if (heading) this.dialog.setAttribute('aria-labelledby', heading.id);
      this.dialog.scrollTop = 0;
      const focusTarget = options.focus || heading || this.dialog;
      focusTarget.focus({ preventScroll: true });
      if (options.track !== false) {
        this.push('step_viewed');
      }
    }

    previousView() {
      return this.history.length ? this.history[this.history.length - 1] : 'hmo';
    }

    goBack() {
      const to = this.history.length ? this.history.pop() : 'hmo';
      /* Leaving a result with a pending not-covered state: keep selections, drop the message. */
      this.showView(to, { back: true });
    }

    openHow() {
      this.showView('how', { focus: this.querySelector('[data-how-close]') });
    }

    closeHow() {
      const to = this.history.length ? this.history.pop() : 'hmo';
      this.showView(to, { back: true });
      const link = this.views[to].querySelector('[data-how-open]');
      if (link) link.focus({ preventScroll: true });
    }

    resetModal() {
      this.history = [];
      this.resetState();
      clearTimeout(this.searchTimer);
      this.searchSeq++;
      this.hmoRadios.forEach((r) => {
        r.checked = false;
        r.closest('.imv2__chk').classList.remove('is-on');
      });
      this.hmoContinue.disabled = true;
      this.stateSelect.selectedIndex = 0;
      this.stateSelect.classList.add('is-empty');
      this.comboInput.value = '';
      this.comboInput.disabled = true;
      this.insuranceField.classList.add('is-disabled');
      this.closeList();
      this.comboList.innerHTML = '';
      this.notCoveredEl.hidden = true;
      this.verifyContinue.disabled = true;
      this.restoreContinue();
      if (this.isleepForm) {
        this.isleepForm.reset();
        this.isleepForm.querySelectorAll('.imv2__form-group').forEach((g) => g.classList.remove('has-error'));
        this.isleepFormStarted = false;
        this.isleepSubmit.disabled = false;
        this.isleepSubmitLabel.textContent = this.isleepSubmitDefault;
      }
      this.views.hmo.classList.add('is-active');
      Object.keys(this.views).forEach((key) => {
        if (key !== 'hmo') this.views[key].setAttribute('inert', '');
        this.views[key].classList.toggle('is-active', key === 'hmo');
      });
      this.views.hmo.removeAttribute('inert');
      this.dialog.setAttribute('data-view-active', 'hmo');
    }

    /* ------------------------------------------------------------------ */
    /* Step 2: state select                                               */
    /* ------------------------------------------------------------------ */

    initState() {
      const select = this.stateSelect;
      select.addEventListener('mousedown', () => this.trackStateOpen());
      select.addEventListener('keydown', (e) => {
        if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].indexOf(e.key) > -1) this.trackStateOpen();
      });
      select.addEventListener('blur', () => {
        this.stateOpenTracked = false;
      });
      select.addEventListener('change', () => {
        this.shippingState = select.value;
        select.classList.remove('is-empty');
        /* Insurance search is state-scoped: enable it now, drop any prior pick. */
        this.comboInput.disabled = false;
        this.insuranceField.classList.remove('is-disabled');
        this.clearPayer();
        this.comboInput.value = '';
        this.notCovered = false;
        this.hideNotCovered();
        this.updateContinue();
      });
    }

    trackStateOpen() {
      if (this.stateOpenTracked) return;
      this.stateOpenTracked = true;
      this.push('state_dropdown_open');
    }

    /* ------------------------------------------------------------------ */
    /* Step 2: insurance combobox (ARIA 1.2 combobox with listbox popup)  */
    /* ------------------------------------------------------------------ */

    initCombo() {
      const input = this.comboInput;

      input.addEventListener('input', () => {
        /* Any edit invalidates the prior pick: a free-text name is not a payer id. */
        this.clearPayer();
        this.notCovered = false;
        this.hideNotCovered();
        this.updateContinue();
        this.scheduleSearch(input.value.trim());
      });
      input.addEventListener('focus', () => this.openListForQuery());
      input.addEventListener('click', () => this.openListForQuery());
      input.addEventListener('keydown', (e) => this.onComboKeydown(e));

      this.comboList.addEventListener('mousedown', (e) => {
        /* Keep focus in the input while picking an option. */
        e.preventDefault();
      });
    }

    openListForQuery() {
      if (this.comboInput.disabled) return;
      const q = this.comboInput.value.trim();
      if (q.length < SEARCH_MIN_CHARS) {
        this.renderOptions([], this.searchHint);
      } else if (this.comboList.hidden && this.results.length) {
        this.renderOptions(this.results);
      }
    }

    scheduleSearch(query) {
      clearTimeout(this.searchTimer);
      this.searchSeq++;
      if (query.length < SEARCH_MIN_CHARS) {
        this.renderOptions([], this.searchHint);
        return;
      }
      this.searchTimer = setTimeout(() => this.searchPayers(query), SEARCH_DEBOUNCE_MS);
    }

    async searchPayers(query) {
      if (!this.workerUrl) {
        this.renderOptions([], this.searchErrorText);
        return;
      }
      const seq = ++this.searchSeq;
      this.renderOptions([], this.searchingText);
      try {
        const res = await fetch(this.workerUrl + '/payers/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: query, limit: 10 }),
        });
        /* Guard every await: a slower earlier query must not overwrite a newer one. */
        if (seq !== this.searchSeq) return;
        if (!res.ok) throw new Error('Payer search failed: ' + res.status);
        const data = await res.json();
        if (seq !== this.searchSeq) return;
        this.results = (data && data.items) || [];
        this.push('insurance_search', { results_count: this.results.length, query_length: query.length });
        this.renderOptions(this.results, this.results.length ? '' : this.noResultsText);
      } catch (err) {
        if (seq !== this.searchSeq) return;
        console.error('Bridge payer search failed:', err);
        this.push('worker_error', { error_phase: 'payer_search' });
        this.renderOptions([], this.searchErrorText);
      }
    }

    /* Rebuild the listbox: optional message, payer options, then "I don't see mine" pinned last.
       Every role="option" emitted here carries data-track (see tracking note at the top). */
    renderOptions(items, message) {
      const list = this.comboList;
      list.innerHTML = '';
      if (message) {
        const li = document.createElement('li');
        li.className = 'imv2__listbox-message';
        li.setAttribute('role', 'presentation');
        li.textContent = message;
        list.appendChild(li);
      }
      items.forEach((p, i) => {
        const li = document.createElement('li');
        li.id = this.comboInput.id + '-opt-' + i;
        li.className = 'imv2__option';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.setAttribute('data-track', 'insurance_selected');
        li.setAttribute('data-selection-source', 'bridge');
        li.setAttribute('data-payer-id', p.id || '');
        li.setAttribute('data-payer-name', p.name || '');
        li.textContent = p.name || '';
        if (p.hint) {
          const hint = document.createElement('span');
          hint.className = 'imv2__option-hint';
          hint.textContent = p.hint;
          li.appendChild(hint);
        }
        li.addEventListener('click', () => this.selectPayer(p.id || '', p.name || ''));
        list.appendChild(li);
      });
      const pinned = document.createElement('li');
      pinned.id = this.comboInput.id + '-opt-not-listed';
      pinned.className = 'imv2__option imv2__option--pinned';
      pinned.setAttribute('role', 'option');
      pinned.setAttribute('aria-selected', 'false');
      pinned.setAttribute('data-track', 'insurance_selected');
      pinned.setAttribute('data-selection-source', 'not_listed');
      pinned.setAttribute('data-payer-name', this.notListedLabel);
      pinned.textContent = this.notListedLabel;
      pinned.addEventListener('click', () => this.selectNotListed());
      list.appendChild(pinned);

      this.activeIndex = -1;
      this.comboInput.removeAttribute('aria-activedescendant');
      list.hidden = false;
      this.comboInput.setAttribute('aria-expanded', 'true');
    }

    closeList() {
      if (!this.comboList) return;
      this.comboList.hidden = true;
      this.comboInput.setAttribute('aria-expanded', 'false');
      this.comboInput.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
    }

    options() {
      return Array.prototype.slice.call(this.comboList.querySelectorAll('[role="option"]'));
    }

    setActive(index) {
      const opts = this.options();
      if (!opts.length) return;
      this.activeIndex = (index + opts.length) % opts.length;
      opts.forEach((o, i) => {
        o.classList.toggle('is-active', i === this.activeIndex);
      });
      const active = opts[this.activeIndex];
      this.comboInput.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }

    onComboKeydown(e) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (this.comboList.hidden) this.openListForQuery();
          this.setActive(this.activeIndex + 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (this.comboList.hidden) this.openListForQuery();
          this.setActive(this.activeIndex - 1);
          break;
        case 'Home':
          if (!this.comboList.hidden) {
            e.preventDefault();
            this.setActive(0);
          }
          break;
        case 'End':
          if (!this.comboList.hidden) {
            e.preventDefault();
            this.setActive(this.options().length - 1);
          }
          break;
        case 'Enter':
          if (!this.comboList.hidden && this.activeIndex > -1) {
            e.preventDefault();
            /* Route through a real click so the delegated tracker records the selection. */
            this.options()[this.activeIndex].click();
          }
          break;
        case 'Tab':
          this.closeList();
          break;
        default:
      }
    }

    selectPayer(id, name) {
      this.payerId = id;
      this.payerName = name;
      this.notListed = false;
      this.notCovered = false;
      this.hideNotCovered();
      this.comboInput.value = name;
      this.results = [];
      this.closeList();
      this.updateContinue();
      this.suppressOpenTrack = document.activeElement !== this.comboInput;
      this.comboInput.focus();
      this.suppressOpenTrack = false;
    }

    selectNotListed() {
      this.payerId = '';
      this.payerName = '';
      this.notListed = true;
      this.comboInput.value = this.notListedLabel;
      this.results = [];
      this.closeList();
      this.push('insurance_not_listed_selected');
      this.showNotCovered('not_listed');
      this.updateContinue();
      this.comboInput.focus();
    }

    clearPayer() {
      this.payerId = '';
      this.payerName = '';
      this.notListed = false;
      this.results = [];
      this.searchSeq++;
    }

    updateContinue() {
      const ready = this.shippingState && this.payerId && !this.notListed && !this.notCovered;
      this.verifyContinue.disabled = !ready;
    }

    restoreContinue() {
      if (this.verifyContinueLabel) this.verifyContinueLabel.textContent = this.verifyContinueDefault;
    }

    showNotCovered(source) {
      this.notCoveredEl.hidden = false;
      this.push('result_viewed', { result_type: 'not_covered', not_covered_source: source });
    }

    hideNotCovered() {
      this.notCoveredEl.hidden = true;
    }

    /* ------------------------------------------------------------------ */
    /* Coverage decision                                                  */
    /* ------------------------------------------------------------------ */

    /* Tracking label only. 'canvas' = the worker routed in-house; whether the state is
       enabled at all is in `reason` (state_not_enabled vs at_capacity, ...). */
    stateGroup(data) {
      if (data && data.route === 'canvas') return 'canvas';
      if (this.asyncStates.indexOf(this.shippingState) > -1) return 'async';
      return 'standard';
    }

    /* Medicare / Medicaid / Medi-Cal patients cannot be seen in house (Canvas), so they
       skip /route entirely (no Bridge call, no capacity reserved) and go straight to the
       Medicare flow chosen by medicare_mode. */
    onVerifyContinue() {
      if (MEDICARE_NAME_RE.test(this.payerName)) {
        this.showMedicare();
        return;
      }
      this.checkCoverage();
    }

    showMedicare() {
      if (this.medicareMode === 'custom') {
        this.showView('medicare_custom');
        this.push('result_viewed', { result_type: 'medicare_custom' });
      } else {
        this.showView('medicare_legacy');
        this.push('result_viewed', { result_type: 'medicare_legacy' });
      }
    }

    /* Match a Bridge payer NAME against the iSleep carrier list for this state. */
    matchesIsleepList() {
      const name = normalize(this.payerName);
      const state = this.shippingState;
      return this.carriers.some((c) => {
        const states = c.states || ['ALL'];
        if (states.indexOf('ALL') === -1 && states.indexOf(state) === -1) return false;
        const keywords = [c.label].concat(c.aliases || []);
        return keywords.some((k) => {
          const needle = normalize(k);
          return needle.trim() !== '' && name.indexOf(needle) > -1;
        });
      });
    }

    async checkCoverage() {
      this.verifyContinue.disabled = true;
      if (this.verifyContinueLabel) this.verifyContinueLabel.textContent = this.checkingText;
      this.push('coverage_check_started');

      let data = null;
      let failed = false;
      if (!this.workerUrl) {
        failed = true;
      } else {
        try {
          const res = await fetch(this.workerUrl + '/route', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              payerId: this.payerId,
              state: this.shippingState,
              payerName: this.payerName,
            }),
          });
          if (!res.ok) throw new Error('Route decision failed: ' + res.status);
          data = await res.json();
        } catch (err) {
          console.error('Route decision failed:', err);
          failed = true;
          this.push('worker_error', { error_phase: 'route' });
        }
      }

      this.restoreContinue();
      this.updateContinue();
      const npCovered = !!(data && data.npCovered);
      const pcCovered = !!(data && data.pcCovered);
      const bridgeCovered = npCovered || pcCovered;
      const isleepMatch = this.matchesIsleepList();
      const group = this.stateGroup(data);
      const base = {
        route: failed ? 'error' : (data && data.route) || '',
        reason: failed ? 'worker_error' : (data && data.reason) || '',
        np_covered: npCovered,
        pc_covered: pcCovered,
        state_group: group,
      };

      if (!failed && data.route === 'canvas' && this.canvasFormUrl) {
        this.push('coverage_check_result', Object.assign({ coverage_source: 'bridge' }, base));
        this.showView('quote');
        this.push('result_viewed', { result_type: 'quote' });
        return;
      }

      if (bridgeCovered || isleepMatch) {
        const source = bridgeCovered ? 'bridge' : 'isleep_list';
        this.push('coverage_check_result', Object.assign({ coverage_source: source }, base));
        this.showAccepted(group === 'async');
        return;
      }

      this.push('coverage_check_result', Object.assign({ coverage_source: 'none' }, base));
      if (failed) {
        this.showView('error');
        this.push('result_viewed', { result_type: 'error' });
        return;
      }
      /* Covered nowhere: inline message, Continue stays disabled until the shopper edits. */
      this.notCovered = true;
      this.updateContinue();
      this.showNotCovered('no_coverage');
    }

    showAccepted(cashFirst) {
      /* Async states lead with cash, so the header switches to the "cash recommended" copy. */
      if (this.views.accepted) {
        this.views.accepted.querySelectorAll('[data-accepted-copy]').forEach((el) => {
          el.hidden = el.getAttribute('data-accepted-copy') !== (cashFirst ? 'async' : 'standard');
        });
      }
      if (this.cards) {
        const cash = this.cards.querySelector('[data-card="cash"]');
        const insurance = this.cards.querySelector('[data-card="insurance"]');
        if (cash && insurance) {
          if (cashFirst) {
            this.cards.insertBefore(cash, insurance);
          } else {
            this.cards.insertBefore(insurance, cash);
          }
        }
      }
      this.showView('accepted');
      this.push('result_viewed', {
        result_type: cashFirst ? 'accepted_cash_first' : 'accepted_insurance_first',
      });
    }

    /* ------------------------------------------------------------------ */
    /* Destinations                                                       */
    /* ------------------------------------------------------------------ */

    redirect(url) {
      if (!url) return;
      if (window.SDFormTracking && window.SDFormTracking.appendIds) {
        window.SDFormTracking.appendIds(url, function (finalUrl) {
          window.location.href = finalUrl;
        });
      } else {
        window.location.href = url;
      }
    }

    /* Canvas: payer name + id + tracking ids appended to the intake URL. */
    submitCanvas() {
      if (!this.canvasFormUrl) return;
      this.converted = true;
      let url = appendParam(this.canvasFormUrl, this.primaryPayorParam, this.payerName);
      url = appendParam(url, this.payerParam, this.payerId);
      this.redirect(url);
    }

    /* Standard insurance intake: tracking ids only, that form does not consume payer params. */
    /* Standard intake vs. the split form. Deterministic per visitor (sticky bucket),
       so the tracked destination and the redirect always agree. */
    resolveStandardIntake() {
      const standard = { url: this.standardIntakeUrl, destination: 'standard_intake' };
      if (!this.intakeSplitUrl || this.intakeSplitPercent <= 0) return standard;
      if (this.intakeSplitStates.length && this.intakeSplitStates.indexOf(this.shippingState) === -1) {
        return standard;
      }
      if (intakeSplitBucket() >= this.intakeSplitPercent) return standard;
      return { url: this.withStandardParams(this.intakeSplitUrl), destination: 'zoho_creator' };
    }

    /* The Creator intake expects the same parameters as the standard intake: copy the
       standard URL's query string (utm_*, SDtreatment, ...) onto the split URL without
       overriding anything the split URL sets itself. Tracking ids are added later by
       redirect() -> SDFormTracking.appendIds, as for every other destination. */
    withStandardParams(url) {
      try {
        const target = new URL(url, window.location.origin);
        const source = new URL(this.standardIntakeUrl, window.location.origin);
        source.searchParams.forEach((value, key) => {
          if (!target.searchParams.has(key)) target.searchParams.set(key, value);
        });
        return target.toString();
      } catch {
        return url;
      }
    }

    submitStandardIntake() {
      const intake = this.resolveStandardIntake();
      if (!intake.url) return;
      this.converted = true;
      this.redirect(intake.url);
    }

    /* partnerID rides along as a CART ATTRIBUTE on a cart permalink (not a query id). */
    cashUrlWithPartner() {
      if (!this.cashCartUrl) return '';
      const pid =
        window.SDFormTracking && window.SDFormTracking.getPartnerId
          ? window.SDFormTracking.getPartnerId()
          : '';
      if (!pid) return this.cashCartUrl;
      const sep = this.cashCartUrl.indexOf('?') > -1 ? '&' : '?';
      return this.cashCartUrl + sep + 'attributes[partnerID]=' + encodeURIComponent(pid);
    }

    goToCash() {
      const url = this.cashUrlWithPartner();
      if (!url) return;
      this.converted = true;
      window.location.href = url;
    }

    /* ------------------------------------------------------------------ */
    /* Legacy Medicare: iSleep contact form (ported from the v1 hybrid)   */
    /* ------------------------------------------------------------------ */

    async submitIsleep() {
      const form = this.isleepForm;
      form.querySelectorAll('.imv2__form-group').forEach((g) => g.classList.remove('has-error'));
      if (!this.validateForm(form)) return;
      this.isleepSubmit.disabled = true;
      this.isleepSubmitLabel.textContent = 'Redirecting…';
      const emailInput = form.querySelector('input[type="email"]');
      const emailHash = emailInput
        ? await this.hashEmail(emailInput.value.trim()).catch(() => '')
        : '';
      /* The referral POST is best-effort: a failed or misconfigured "Referral form
         endpoint" must never strand the patient. Log it and still send them on. */
      let submitted = false;
      try {
        await this.submitIsleepForm();
        submitted = true;
      } catch (err) {
        console.error('iSleep referral submission failed:', err);
        this.push('worker_error', { error_phase: 'isleep_form' });
      }
      if (submitted) this.push('isleep_form_submitted', { user_email_hash: emailHash });
      if (this.dreemRedirectUrl) {
        this.converted = true;
        this.push('isleep_redirect_initiated', { redirect_url: this.dreemRedirectUrl, referral_submitted: submitted });
        window.location.href = this.dreemRedirectUrl;
        return;
      }
      this.showView('error');
      this.push('result_viewed', { result_type: 'error' });
      this.isleepSubmit.disabled = false;
      this.isleepSubmitLabel.textContent = this.isleepSubmitDefault;
    }

    async submitIsleepForm() {
      const form = this.isleepForm;
      const nameInput = form.querySelector('input[name="contact[name]"]');
      const emailInput = form.querySelector('input[type="email"]');
      const phoneInput = form.querySelector('input[name="contact[phone]"]');
      const response = await fetch(this.dreemWorkerUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: nameInput ? nameInput.value.trim() : '',
          email: emailInput ? emailInput.value.trim() : '',
          phone: phoneInput ? phoneInput.value.trim() : '',
          medicare_type: this.payerName || 'unknown',
          state: this.shippingState,
          referral_reason: 'medicare',
          source: 'sleepdoctor_insurance_modal_v2',
        }),
      });
      if (!response.ok) throw new Error('iSleep referral failed: ' + response.status);
      return response.json();
    }

    validateForm(form) {
      let valid = true;
      form.querySelectorAll('input').forEach((input) => {
        const group = input.closest('.imv2__form-group');
        const value = input.value.trim();
        const bad =
          value === '' || (input.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value));
        if (bad) {
          if (group) group.classList.add('has-error');
          input.setAttribute('aria-invalid', 'true');
          valid = false;
        }
      });
      if (!valid) {
        const firstBad = form.querySelector('[aria-invalid="true"]');
        if (firstBad) firstBad.focus();
      }
      return valid;
    }

    async hashEmail(email) {
      const encoded = new TextEncoder().encode(email.toLowerCase().trim());
      const buffer = await crypto.subtle.digest('SHA-256', encoded);
      return Array.from(new Uint8Array(buffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    }
  }

  if (!customElements.get('insurance-modal-v2')) {
    customElements.define('insurance-modal-v2', InsuranceModalV2);
  }
})();
